import gql from 'graphql-tag';
import { graphql } from '@apollo/react-hoc';
import React from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import get from 'lodash/get';
import { compose } from 'react-recompose';
import { injectIntl, FormattedMessage } from 'react-intl';
import { SimpleLoadingComponent } from '../../utils/apollo';
import { queryErrorsToWarnings } from '../../components/GraphQLError';

import Header from '../../components/Header';
import Dropdown from '../../components/Dropdown';
import Toast from '../../components/Toast';
import AMSchoolYearConnections from './school-year-connections';
import AMSinceLastExportConnections from './since-last-export-connections';
import AMSinceRecentConnections from './since-recent-connections';
import DownloadConnections, { UNEXPORTED } from './download-connections';
import withUpdateLastDownloadTime from './updateActiveMatchConnectionsLastDownloadedDatetimeMutation';
import logActiveMatchConnectionsDownload from './logActiveMatchConnectionsDownloadMutation';
import * as constants from '../Authorization/constants';

import {
  SCHOOL_YEARS,
  LIMIT,
  SHOW_YEAR,
  SHOW_LAST_EXPORT,
  SHOW_RECENT,
  HISTORICAL_CHOICES,
  SINCE_LAST_EXPORT,
  HISTORICAL,
  BY_SCHOOL_YEAR,
} from './constants';
import messages from './messages';
import './styles.css';

export class AMPlusConnections extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.object,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
      formatDate: PropTypes.func.isRequired,
      formatTime: PropTypes.func.isRequired,
    }).isRequired,
    updateLastDownloadTime: PropTypes.func.isRequired,
    logActiveMatchConnectionsDownload: PropTypes.func,
    warnings: PropTypes.array,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedSchoolYear: { text: null, value: null },
      displayContainer: SHOW_LAST_EXPORT,
      recentDays: { text: null, value: null },
      shouldMoveFocusToDownload: false,
    };
    this.downloadRef = undefined;
  }

  componentDidMount() {
    document.addEventListener('keydown', this.keyDownHandle);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.keyDownHandle);
  }

  keyDownHandle = (e) => {
    // presses tab key
    if (e.keyCode === 9) {
      this.setState({ shouldMoveFocusToDownload: true });
    }
    // presses tab + shift key
    if (e.keyCode === 9 && e.shiftKey) {
      this.setState({ shouldMoveFocusToDownload: false });
    }
  }

  updateLastDownloadTimestamp = () => {
    const { updateLastDownloadTime } = this.props;
    updateLastDownloadTime();
  };

  changeSchoolYear = (e, { text, value }) => {
    this.setState({
      selectedSchoolYear: { text, value },
      displayContainer: SHOW_YEAR,
      recentDays: { text: null, value: null },
    });
  };

  changeRecentDays = (e, { text, value }) => {
    this.setState({
      recentDays: { value, text },
      displayContainer: SHOW_RECENT,
      selectedSchoolYear: { text: null, value: null },
    });
  };

  displayUnexportedConnectionsView = () => {
    this.setState({
      displayContainer: SHOW_LAST_EXPORT,
      selectedSchoolYear: { text: null, value: null },
      recentDays: { text: null, value: null },
    });
  };

  onChangeDropDown = (e, { value }) => {
    if (value === SINCE_LAST_EXPORT) {
      this.displayUnexportedConnectionsView();
    } else if (HISTORICAL_CHOICES.some((c) => c.value === value)) {
      const choiceText = this.props.intl.formatMessage(messages.historicalDays, { days: value });
      this.changeRecentDays(e, { text: choiceText, value });
    } else if (SCHOOL_YEARS.some((v) => v.value === value)) {
      const choiceText = SCHOOL_YEARS.find((v) => v.value === value);
      this.changeSchoolYear(e, { text: choiceText.text, value });
    }
  }

  setDropdownBtnRef = (btnRef) => {
    this.downloadRef = btnRef;
  }

  onBlurDropdown = (e, f) => {
    e.preventDefault();
    if (this.state.shouldMoveFocusToDownload) {
      this.downloadRef.focus();
    }
  }

  prepareDropDownMenu = (key, message) => ({
    key,
    text: (<span styleName="uppercase">{message}</span>),
    disabled: true,
  });

  prepareDropDownOptions = (sourceArray, type) => (sourceArray.map((r) =>
    (
      {
        key: r.value,
        text: type === messages.historical.defaultMessage ?
          this.props.intl.formatMessage(messages.historicalDays, { days: r.value }) :
          r.text,
        value: r.value,
      }
    ))
  );

  prepareSinceLastExportOption = (
    lastDownloadedDatetime,
    totalCountSinceLastExport,
    formattedLastDownloadedDate,
    formattedLastDownloadedTime,
  ) => ({
    key: SINCE_LAST_EXPORT,
    text: lastDownloadedDatetime ?
      this.props.intl.formatMessage(messages.connLastExport, {
        count: totalCountSinceLastExport,
        date: formattedLastDownloadedDate,
        time: formattedLastDownloadedTime,
      }) :
      (<FormattedMessage {...messages.noExportsYet} />),
    value: SINCE_LAST_EXPORT,
  });

  prepareDropDownItems = (sinceLastExportOptions) => {
    const sinceLastExportMenu = this.prepareDropDownMenu(messages.sinceLastExport.defaultMessage, messages.sinceLastExport.defaultMessage);
    const historicalMenu = this.prepareDropDownMenu(HISTORICAL, messages.historical.defaultMessage);
    const bySchoolYearMenu = this.prepareDropDownMenu(BY_SCHOOL_YEAR, messages.bySchoolYear.defaultMessage);
    const schoolYearOptions = this.prepareDropDownOptions(SCHOOL_YEARS, messages.bySchoolYear.defaultMessage);
    const historicalOptions = this.prepareDropDownOptions(HISTORICAL_CHOICES, messages.historical.defaultMessage);
    return [
      sinceLastExportMenu,
      sinceLastExportOptions,
      historicalMenu,
      ...historicalOptions,
      bySchoolYearMenu,
      ...schoolYearOptions,
    ];
  };

  render() {
    const { intl, viewer, warnings } = this.props;
    const temporarilyUnavailable = (warnings && warnings.length > 0);

    const { selectedSchoolYear, displayContainer, recentDays } = this.state;
    const totalCountSinceLastExport = get(viewer, 'amStudentConnectionsSinceLastExport.pagination.totalCount', 0);
    let lastDownloadedDatetime = get(viewer, 'heAccount.activeMatchConnectionsLastDownloadedDatetime');
    if (!lastDownloadedDatetime) {
      const subscriptionStartDate =
        (get(viewer, 'heAccount.activeSubscriptionInfo').find((sub) => sub.name === constants.AM_PLUS_SUBSCRIPTION) ||
        /* istanbul ignore next: all accounts should have subscriptions defined, but this is for safety */ {}).startDate;
      // always use 12 AM as the time component of the subscription start
      lastDownloadedDatetime = (new Date(subscriptionStartDate)).setHours(0, 0, 0, 0);
    }
    const formattedLastDownloadedDate = lastDownloadedDatetime ?
      intl.formatDate(lastDownloadedDatetime, {
        weekday: 'short',
        month: 'short',
        year: 'numeric',
        day: '2-digit',
      }) :
      null;
    const formattedLastDownloadedTime = lastDownloadedDatetime ?
      intl.formatTime(lastDownloadedDatetime, { hour12: true }) :
      null;
    const selectedSchoolYearValue = displayContainer === SHOW_LAST_EXPORT ? UNEXPORTED : selectedSchoolYear.value;
    let dropdownText = intl.formatMessage(messages.sinceLastExport);
    if (displayContainer === SHOW_YEAR) {
      dropdownText = intl.formatMessage(messages.schoolYear, { year: selectedSchoolYear.text });
    } else if (displayContainer === SHOW_RECENT) {
      dropdownText = intl.formatMessage(messages.historicalHeading, { txt: recentDays.text });
    }
    // only update the activeMatchConnectionsLastDownloadedDatetime if they are downloading "since the last export"
    const onDownload = (displayContainer === SHOW_LAST_EXPORT) ? this.updateLastDownloadTimestamp : noop;
    const sinceLastExportOptions = this.prepareSinceLastExportOption(
      lastDownloadedDatetime,
      totalCountSinceLastExport,
      formattedLastDownloadedDate,
      formattedLastDownloadedTime,
    );
    return (
      <div styleName="container">
        <Header as="h1" size="huge" id="pageTitle"><FormattedMessage {...messages.connections} /></Header>
        <Toast
          active={temporarilyUnavailable}
          title="Temporarily Unavailable"
          kind="error"
          description={intl.formatMessage(messages.temporarilyUnavailable)}
          onDeactivate={noop}
        />
        {!temporarilyUnavailable && (
          <Dropdown
            aria-label="Select timeframe to download student connections"
            text={dropdownText}
            selection
            selectOnNavigation={false}
            styleName="school-year-dropdown width300"
            options={this.prepareDropDownItems(sinceLastExportOptions)}
            onChange={this.onChangeDropDown}
            defaultValue={sinceLastExportOptions.value}
            onBlur={this.onBlurDropdown}
          />
        )}
        {!temporarilyUnavailable && (
          <DownloadConnections
            setDropdownBtnRef={this.setDropdownBtnRef}
            onDownload={onDownload}
            schoolYear={selectedSchoolYearValue}
            downloadType={displayContainer}
            recentDays={recentDays.value}
            logActiveMatchConnectionsDownload={this.props.logActiveMatchConnectionsDownload}
          />
        )}
        {displayContainer === SHOW_YEAR &&
        <AMSchoolYearConnections schoolYear={selectedSchoolYear.value} />}
        {displayContainer === SHOW_LAST_EXPORT && <AMSinceLastExportConnections />}
        {displayContainer === SHOW_RECENT &&
          <AMSinceRecentConnections recentDays={recentDays.value} />}
      </div>
    );
  }
}

export const AMPlusConnectionsComponent = injectIntl(AMPlusConnections);

const query = gql`
  query amStudentConnections ($limit: Int, $page: Int) {
    viewer {
      id
      amStudentConnectionsSinceLastExport(page: $page, limit: $limit) {
        pagination {
          totalCount
        }
      }
      heAccount {
        id
        activeMatchConnectionsLastDownloadedDatetime
        activeSubscriptionInfo {
          name
          startDate
        }
      }
    }
  }
`;

const graphqlData = graphql(query, {
  options: (props) => ({
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    variables: {
      page: 1,
      limit: LIMIT,
    },
  }),
  props: compose(queryErrorsToWarnings(['amStudentConnectionsSinceLastExport']), (props) => props),
});

export default SimpleLoadingComponent(
  graphqlData,
  withUpdateLastDownloadTime,
  logActiveMatchConnectionsDownload,
)(AMPlusConnectionsComponent);
